import { FC, PropsWithChildren, ReactElement } from 'react';

import { DefaultHelmet } from '../../../../../compositions';
import { AppRoute, appRoutes } from '../../../../../entities/Routing/Routing';
import { Space } from '../../../../../entities/Space/Space';
import { generateBreadcrumbStructuredData } from '../../../../../helpers/structuredData';
import { replaceUrlParamKeysWithValues } from '../../../../../helpers/url';
import { useTrans } from '../../../../../hooks';

interface SpaceDetailHelmetProps extends PropsWithChildren {
    space: Space;
}

export const SpaceDetailHelmet: FC<SpaceDetailHelmetProps> = ({
    space,
    children,
}): ReactElement => {
    const trans = useTrans();

    const siteUrl = trans('company.url');

    const metaTitle = space.seo
        ? space.seo.title
        : space.name;

    const metaDescription = space.seo
        ? space.seo.description
        : metaTitle;

    const metaKeywords = space.seo
        ? space.seo.keywords
        : undefined;

    const metaImage = space.seo
        ? space.seo.image
        : space.image;

    const spacePath = trans(appRoutes[AppRoute.brandDetail].path);
    const spaceUrl = replaceUrlParamKeysWithValues(spacePath, {
        slug: space.slug,
    });

    const breadcrumbs = generateBreadcrumbStructuredData(siteUrl, [
        {
            label: trans(appRoutes[AppRoute.spacesOverview].label),
            href: trans(appRoutes[AppRoute.spacesOverview].path),
        },
        {
            label: space.name,
            href: spaceUrl,
        },
    ]);

    return (
        <DefaultHelmet
            title={metaTitle}
            description={metaDescription}
            keywords={metaKeywords}
            image={metaImage}
            canonical={space.seo?.canonicalURL}
        >
            <script type="application/ld+json">
                {breadcrumbs}
            </script>

            {children}
        </DefaultHelmet>
    );
};
